"use client";

import { useQuery } from "@tanstack/react-query";
import type { ProductReview } from "@trydig/reviews-io";
import { Star } from "lucide-react";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { Skeleton } from "@/components/ui/Skeleton";
import { REVIEWS_STORE_ID } from "./constants";
import styles from "./Reviews.module.css";

const ONE_WEEK_IN_MS = 1000 * 60 * 60 * 24 * 7;

const fetchAggregatedRatings = async (
	productIds: string[],
): Promise<ProductReview[]> => {
	const res = await fetch(
		`https://api.reviews.io/product/rating-batch?sku=${productIds.join(
			";",
		)}&store=${REVIEWS_STORE_ID}`,
	);
	if (!res.ok) return [];
	return (await res.json()) as ProductReview[];
};

/**
 * Fetches the ratings for every passed id and combines them into a single
 * rating (sum of review counts, count-weighted average). Passing a single
 * product's ids yields that product's rating; passing a product and all of its
 * colour variants yields the combined rating.
 */
const useAggregatedProductReview = (productIds: string[]) => {
	const query = useQuery({
		queryKey: ["aggregatedProductReview", [...productIds].sort().join(";")],
		queryFn: () => fetchAggregatedRatings(productIds),
		staleTime: ONE_WEEK_IN_MS,
		enabled: productIds.length > 0,
	});

	// Only count rows with a valid numeric average so a missing/malformed
	// `average_rating` can't turn the aggregate into NaN.
	const validResults = (query.data ?? []).filter(
		(r) =>
			(r.num_ratings || 0) > 0 && Number.isFinite(Number(r.average_rating)),
	);
	const reviewCount = validResults.reduce((sum, r) => sum + r.num_ratings, 0);
	const weightedSum = validResults.reduce(
		(sum, r) => sum + Number(r.average_rating) * r.num_ratings,
		0,
	);

	return {
		rating: reviewCount > 0 ? weightedSum / reviewCount : 0,
		reviewCount,
		isLoading: query.isLoading,
		error: query.error,
	};
};

const ReviewStars = ({
	rating,
	reviewCount,
	variant = "default",
	isLoading = false,
}: {
	rating: number;
	reviewCount: number;
	variant?: "default" | "extended";
	isLoading?: boolean;
}) => {
	if (isLoading) {
		if (variant === "extended") {
			return (
				<span className={styles.root}>
					{Array.from({ length: 5 }).map((_, i) => (
						<Skeleton key={i} fitContent>
							<Star width={12} height={12} />
						</Skeleton>
					))}
					<Skeleton fitContent>
						<p>(0 reviews)</p>
					</Skeleton>
				</span>
			);
		}
		return (
			<span className={styles.root}>
				<Skeleton fitContent>
					<Star />
				</Skeleton>

				<Skeleton fitContent>
					<p>5.0</p>
				</Skeleton>
			</span>
		);
	}

	if (variant === "extended") {
		return (
			<span className={styles.root}>
				{[1, 2, 3, 4, 5].map((starIndex) => {
					const fillPercentage = Math.max(
						0,
						Math.min(1, rating - starIndex + 1),
					);
					const isFilled = fillPercentage > 0;

					return (
						<Star
							key={starIndex}
							width={12}
							height={12}
							fill={isFilled ? "black" : "none"}
							stroke="black"
							style={{
								opacity:
									fillPercentage === 1 ? 1 : fillPercentage > 0 ? 0.5 : 1,
							}}
						/>
					);
				})}
				<p>({reviewCount} reviews)</p>
			</span>
		);
	}

	return (
		<span className={styles.root}>
			<Star fill="black" width={12} height={12} />
			<p>{rating.toFixed(1)}</p>
		</span>
	);
};

/**
 * Renders the star rating for a set of reviews.io ids, aggregating across all
 * of them. Callers derive the ids with `generateReviewsIdentifier` (single
 * product) or `generateVariantReviewsIdentifiers` (product + colour variants).
 */
const ProductRatingInner = ({
	productIds,
	variant = "default",
}: {
	productIds: string[];
	variant?: "default" | "extended";
}) => {
	const { rating, reviewCount, isLoading, error } =
		useAggregatedProductReview(productIds);

	if (isLoading)
		return (
			<ReviewStars isLoading rating={0} reviewCount={0} variant={variant} />
		);

	if (error || reviewCount === 0) return <span className={styles.root} />;

	return (
		<ReviewStars rating={rating} reviewCount={reviewCount} variant={variant} />
	);
};

export const ProductRating = (props: {
	productIds: string[];
	variant?: "default" | "extended";
}) => {
	// The rating is decorative — never let a render error take down the
	// surrounding product page or listing grid.
	return (
		<ErrorBoundary>
			<ProductRatingInner {...props} />
		</ErrorBoundary>
	);
};
